import { FC, useState, useEffect, MouseEvent, useCallback } from 'react'
import { toast } from 'react-toastify'
import 'react-tagsinput/react-tagsinput.css'
import TagsInput from 'react-tagsinput'
import classNames from 'classnames'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Libraries, useJsApiLoader } from '@react-google-maps/api'
import { CAMPAIGNS_ROUTE } from 'src/constants/routes'
import en from 'src/constants/en'
import { COLORS } from 'src/constants/colors'
import { useUpdateRelevancy } from 'src/reactQuery/useUpdateRelevancy'
import { ReactComponent as LinkIcon } from 'src/assets/svg/link-white.svg'
import { ENTER_KEY_CODE, MAX_TAGS, TARGETS } from 'src/constants/campaigns'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import useCampaignNameInput from 'src/hooks/useCampaignNameInput'
import { CampaignInput } from 'src/components/CampaignInput'
import { getTimezoneOffsetInHours } from 'src/utils/date'
import { convertArrayToLowerCase } from 'src/utils/convertArrayToLowerCase'
import { CampaignPayloadKeyword } from 'src/services/__generated__/api'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { useCreateCampaignMutation } from 'src/reactQuery/useCreateCampaignMutation'
import { BackNavigationButton } from 'src/components/BackNavigationButton'
import { Tag } from 'src/components/Tag'
import Button from 'src/components/Button'
import { AddKeywordInput } from 'src/components/AddKeywordInput'
import { IrrelevantKeywordsInput } from 'src/components/IrrelevantKeywordsInput'
import { cn } from 'src/utils/cn'
import KeywordFormCard from './KeywordFormCard'
import styles from './addNewCampaign.module.scss'
import TargetPopup from './TargetPopup/Index'
import TimezoneSelectInput from './TimezoneSelectInput'
import GoogleTripadvisorPopup from './TargetPopup/GoogleTripadvisorPopup'
import hexToRgbA from '../../utils/hexToRgba'
import { removeFromArray } from '../../utils/removeFromArray'

export type TargetAndExcludeState = {
  [K in (typeof TARGETS)[number]]: string[]
}

export const defaultLinksState = TARGETS.reduce((acc, target) => {
  acc[target] = []
  return acc
}, {} as TargetAndExcludeState)

// Review-only targets hidden from the social-listening campaign flow (create
// and edit). They remain valid for other features (e.g. Review Analysis).
const EXCLUDED_TARGETS = ['Google Reviews', 'Tripadvisor', 'Booking']
export const CAMPAIGN_TARGETS = TARGETS.filter(
  (target) => !EXCLUDED_TARGETS.includes(target)
)

const googleMapLibraries: Libraries = ['places']

export const AddNewCampaign: FC = () => {
  const [timezoneOffset, setTimezoneOffset] = useState<number>(
    getTimezoneOffsetInHours()
  )

  const [timezoneName, setTimezoneName] = useState<string>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )

  const [addedTargetsArr, setAddedTargetsArr] = useState<string[]>([])

  const [activeTarget, setActiveTarget] = useState<number>(-1)

  const [targetLinkValues, setTargetLinkValues] =
    useState<TargetAndExcludeState>(defaultLinksState)

  const [excludeLinkValues, setExcludeLinkValues] =
    useState<TargetAndExcludeState>(defaultLinksState)

  const [currentKeywordInput, setCurrentKeywordInput] = useState<string>('')

  // TODO: uncomment after relevancy functionality is ready
  // const [currentRelevancyKeyword, setCurrentRelevancyKeyword] =
  //   useState<Keyword | null>(null)

  const [addedKeywordsArr, setAddedKeywordsArr] = useState<string[]>([])

  const [registeredKeywordsArr, setRegisteredKeywordsArr] = useState<
    CampaignPayloadKeyword[]
  >([])

  const [irrelevantKeywords, setIrrelevantKeywords] = useState<string[]>([])

  const [campaignCategory, setCampaignCategory] = useState<string>('')

  const [draft, setDraft] = useState(false)

  const navigate = useNavigate()

  const backURL = useCustomLink(CAMPAIGNS_ROUTE)

  const [tags, setTags] = useState<string[]>([])

  const [tagInput, setTagInput] = useState<string>('')

  const relevancyMutation = useUpdateRelevancy()

  const [campaignName, setCampaignName] = useCampaignNameInput('')

  const mutation = useCreateCampaignMutation({
    onSuccess: () => {
      navigate(backURL)
    },
  })

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyCHXzLw7PgGIGn6CsrHZty0y2gdscdgBlw',
    libraries: googleMapLibraries,
  })

  // Reset form when component mounts to ensure clean state for new campaigns
  useEffect(() => {
    setTimezoneOffset(getTimezoneOffsetInHours())
    setTimezoneName(Intl.DateTimeFormat().resolvedOptions().timeZone)
    setAddedTargetsArr([])
    setActiveTarget(-1)
    setTargetLinkValues(defaultLinksState)
    setExcludeLinkValues(defaultLinksState)
    setCurrentKeywordInput('')
    setAddedKeywordsArr([])
    setRegisteredKeywordsArr([])
    setIrrelevantKeywords([])
    setDraft(false)
    setTags([])
    setCampaignName('')
  }, [])

  function addNewKeyword() {
    const keyword = currentKeywordInput

    const keywordArray = keyword
      .trim()
      .split(',')
      .map((item) => item.trim())

    const isAnyKeywordEmpty = keywordArray.some((item) => item.length === 0)

    if (addedKeywordsArr.includes(keyword)) {
      toast.error('Keyword you entered is already in use')

      return
    }

    if (isAnyKeywordEmpty) {
      toast.error(en.KEYWORD_ERROR_EMPTY)

      return
    }

    if (convertArrayToLowerCase(tags).includes(keyword.toLowerCase())) {
      toast.error(en.KEYWORD_ERROR_NOT_SAME_TAG)

      return
    }

    // Newly added keywords show at the top of the list.
    setAddedKeywordsArr([...keywordArray, ...addedKeywordsArr])

    setCurrentKeywordInput('')

    toast.success('Keyword is added successfully')
  }

  const handleAddTags = (updatedTags: string[]) => {
    const isRemoving = updatedTags.length < tags.length

    if (isRemoving) {
      setTags(updatedTags)

      return
    }

    const latestTag = updatedTags.at(-1)?.trim() ?? ''
    const lowerCaseLatestTag = latestTag.toLowerCase()

    updatedTags.splice(-1, 1, latestTag)

    if (convertArrayToLowerCase(tags).includes(lowerCaseLatestTag)) {
      toast.error(en.TAG_ERROR_ALREADY_IN_USE)

      return
    }

    if (updatedTags.length > MAX_TAGS) {
      toast.error(en.TAG_ERROR_LIMIT)

      return
    }

    if (lowerCaseLatestTag && lowerCaseLatestTag.split(' ').length > 3) {
      toast.error(en.TAG_ERROR_TOO_MANY_WORDS)

      return
    }

    let isTagUsedAsKeyword = false
    let isTagUsedAsPositiveSentiment = false
    let isTagUsedAsNegativeSentiment = false

    registeredKeywordsArr.forEach(({ name, negative, positive }) => {
      if (name?.toLowerCase() === lowerCaseLatestTag) {
        isTagUsedAsKeyword = true
      }

      if (
        convertArrayToLowerCase(negative || []).includes(lowerCaseLatestTag)
      ) {
        isTagUsedAsNegativeSentiment = true
      }

      if (
        convertArrayToLowerCase(positive || []).includes(lowerCaseLatestTag)
      ) {
        isTagUsedAsPositiveSentiment = true
      }
    })

    if (isTagUsedAsKeyword) {
      toast.error(en.TAG_ERROR_ALREADY_USED_AS_KEYWORD)

      return
    }

    if (isTagUsedAsNegativeSentiment) {
      toast.error(en.TAG_ERROR_ALREADY_USED_AS_NEGATIVE_SENTIMENT)

      return
    }

    if (isTagUsedAsPositiveSentiment) {
      toast.error(en.TAG_ERROR_ALREADY_USED_AS_POSITIVE_SENTIMENT)

      return
    }

    toast.success(en.TAG_SUCCESS_ADDED)
    setTags(updatedTags)
    setTagInput('')
  }

  const handleCreateTag = () => {
    if (!tagInput.trim()) {
      return
    }

    handleAddTags([...tags, tagInput])
  }

  // Tag body: just toggle the target on/off (the popup stays closed).
  function addTargets(index: number, val?: string) {
    if (val === undefined) {
      return
    }

    if (addedTargetsArr.includes(val)) {
      const arr = removeFromArray(addedTargetsArr, val)
      setAddedTargetsArr([...arr])

      if (activeTarget === index) {
        setActiveTarget(-1)
      }
    } else {
      setAddedTargetsArr([...addedTargetsArr, val])
    }
  }

  // Little circle icon: toggle the page/link popup (selecting the target first).
  function openTargetPopup(index: number, val?: string) {
    if (val === undefined) {
      return
    }

    // Clicking the icon again closes the popup.
    if (activeTarget === index) {
      setActiveTarget(-1)
      return
    }

    if (!addedTargetsArr.includes(val)) {
      setAddedTargetsArr([...addedTargetsArr, val])
    }

    setActiveTarget(index)
  }

  function handleChangeKeywordSentimentsValue(val: CampaignPayloadKeyword) {
    let tempArr = [...registeredKeywordsArr]

    const index = tempArr.findIndex((item) => item.name === val.name)

    if (index === -1) {
      tempArr = [...tempArr, val]
    } else {
      tempArr = tempArr.filter((item, i) => i !== index)
      tempArr.push(val)
    }

    setRegisteredKeywordsArr(tempArr)

    // TODO: uncomment after relevancy functionality is ready
    // setCurrentRelevancyKeyword(val)

    relevancyMutation.mutate({
      keyword: val.name,
      negatives: val.negative,
      positives: val.positive,
    })
  }

  // TODO: uncomment after relevancy functionality is ready
  /*
  const handleRelevancySuggestionClick = (
    item: FetchRelevancyDataSuggestionItem,
    positivity: boolean
  ) => {
    if (!currentRelevancyKeyword) {
      return
    }

    const { from, to } = item
    const { negative, positive } = currentRelevancyKeyword

    if (positivity && positive) {
      const repeatsIndex = positive.findIndex((sentiment) => sentiment === to)
      const updatedIndex = positive.findIndex((sentiment) => sentiment === from)

      if (repeatsIndex === -1) {
        positive[updatedIndex] = to
      } else {
        positive.splice(updatedIndex, 1)
      }
    } else if (negative) {
      const repeatsIndex = negative.findIndex((sentiment) => sentiment === to)
      const updatedIndex = negative.findIndex((sentiment) => sentiment === from)

      if (repeatsIndex === -1) {
        negative[updatedIndex] = to
      } else {
        negative.splice(updatedIndex, 1)
      }
    }

    handleChangeKeywordSentimentsValue({
      ...currentRelevancyKeyword,
      negative,
      positive,
    })
  }
  */

  async function addNewCampaign() {
    if (!campaignCategory) {
      toast.error('Please select a campaign category')

      return
    }

    if (addedTargetsArr.length === 0) {
      toast.error(en.EMPTY_TARGETS_ERROR)

      return
    }

    const payload = {
      categories: campaignCategory ? [campaignCategory] : [],
      from: moment().toISOString(),
      irrelevant_keywords: irrelevantKeywords,
      keywords: registeredKeywordsArr,
      name: campaignName,
      status: !draft ? 'active' : 'draft',
      tags,
      targets: addedTargetsArr,
      timezone: timezoneOffset,
      timezoneName,
      to: new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      ).toISOString(),
    } as any

    for (const target of addedTargetsArr) {
      if (targetLinkValues[target]?.length > 0) {
        payload[`target_${target === 'Facebook' ? 'fb' : target}_pages`] =
          targetLinkValues[target]
      }
    }
    for (const target of addedTargetsArr) {
      if (excludeLinkValues[target]?.length > 0) {
        payload[`exclude_${target === 'Facebook' ? 'fb' : target}_pages`] =
          excludeLinkValues[target]
      }
    }

    mutation.mutate(payload)
  }

  const handleSaveCampaign = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    addNewCampaign()
  }

  const handleSaveAsDraft = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setDraft(true)
  }

  const handleDeleteKeyword = (keyword: string) => {
    const temp = removeFromArray(addedKeywordsArr, keyword)

    setAddedKeywordsArr(temp)

    const index = registeredKeywordsArr.findIndex(
      (item) => item.name === keyword
    )

    const tempRegistered = registeredKeywordsArr.slice()

    if (index !== -1) {
      tempRegistered.splice(index, 1)
    }

    setRegisteredKeywordsArr(tempRegistered)
  }

  const handleKeywordUpdate = (keyword: string, index: number): void => {
    const previousKeyword = addedKeywordsArr[index]

    setAddedKeywordsArr((prevKeywords) => {
      const temp = [...prevKeywords]

      temp[index] = keyword

      return temp
    })

    // Match by name, not index: the added/registered arrays can be ordered
    // differently (new keywords prepend; sentiment edits reorder registered).
    setRegisteredKeywordsArr((prevKeywords) =>
      prevKeywords.map((item) =>
        item.name === previousKeyword ? { ...item, name: keyword } : item
      )
    )
  }

  const onSelectAllTargets = () => {
    setAddedTargetsArr(
      addedTargetsArr.length === CAMPAIGN_TARGETS.length ? [] : CAMPAIGN_TARGETS
    )
  }

  const handleAddIrrelevantKeyword = (keyword: string) => {
    setIrrelevantKeywords([...irrelevantKeywords, keyword])
  }

  const handleRemoveIrrelevantKeyword = (keyword: string) => {
    setIrrelevantKeywords(irrelevantKeywords.filter((k) => k !== keyword))
  }

  useEffect(() => {
    if (draft !== false) addNewCampaign()
  }, [draft])

  useEffect(() => {
    setDraft(false)
  }, [])

  const quantity = useCallback(
    (item: string) => {
      if (targetLinkValues[item]?.length > 0) {
        return '+' + targetLinkValues[item].length.toString()
      } else if (excludeLinkValues[item]?.length > 0) {
        return '-' + excludeLinkValues[item].length.toString()
      }
      return '0'
    },
    [targetLinkValues, excludeLinkValues]
  )

  return (
    <div className={styles.addNewCampaign}>
      <BackNavigationButton title={en.createNewCampaign} url={backURL} />

      <div className={styles.body}>
        <form>
          <div className={styles.formSectionNarrow}>
            <div className={styles.sectionLabel}>{en.campaignName}</div>

            <CampaignInput
              onChange={setCampaignName}
              placeholder={en.enterNameOfCampaign}
              themeName={campaignName}
            />
          </div>

          <div
            className={classNames(
              styles.formSectionNarrow,
              styles.timezoneSection
            )}
          >
            <div className={styles.sectionLabel}>{en.timezone}</div>

            <TimezoneSelectInput
              onChange={(offset, name) => {
                setTimezoneOffset(offset)
                setTimezoneName(name)
              }}
              timezoneName={timezoneName}
            />
          </div>

          <div className={styles.formSection}>
            <div className={styles.sectionLabel}>
              Campaign Category <span style={{ color: '#FF8080' }}>*</span>
            </div>
            <div className={styles.categoryCards}>
              {[
                'Brand Management',
                'Competitor Analysis',
                'Market Research',
              ].map((cat) => (
                <button
                  className={`${styles.categoryCard} ${campaignCategory === cat ? styles.selected : ''}`}
                  key={cat}
                  onClick={() =>
                    setCampaignCategory(campaignCategory === cat ? '' : cat)
                  }
                  type="button"
                >
                  <span className={styles.radioCircle}>
                    {campaignCategory === cat && (
                      <span className={styles.radioInner} />
                    )}
                  </span>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formSection}>
            <div
              className={cn(
                styles.sectionLabel,
                'flex items-center justify-between'
              )}
            >
              <span>{en.target}</span>

              <button
                className={cn('text-malibu', 'text-[14px]')}
                onClick={() => onSelectAllTargets()}
                type="button"
              >
                <span>
                  {addedTargetsArr.length === CAMPAIGN_TARGETS.length
                    ? en.unSelectAll
                    : en.selectAll}
                </span>
              </button>
            </div>

            <div className={styles.targets}>
              {CAMPAIGN_TARGETS.map((item, index) => (
                <div
                  key={index}
                  style={{ display: 'inline-block', position: 'relative' }}
                >
                  <Tag
                    backgroundColor={
                      addedTargetsArr.includes(item)
                        ? COLORS.malibu
                        : hexToRgbA(COLORS.periwinkle, 0.5)
                    }
                    color={
                      addedTargetsArr.includes(item)
                        ? COLORS.white
                        : COLORS.shark
                    }
                    cursor="pointer"
                    fontSize="14px"
                    fontWeight={500}
                    handleClick={(value) => addTargets(index, value)}
                    height="36px"
                    iconSVG={LinkIcon}
                    key={item}
                    label={platformDisplayLabel(item)}
                    lineHeight="16.41px"
                    onIconClick={() => openTargetPopup(index, item)}
                    padding={
                      quantity(item) !== '0'
                        ? '10px 6px 10px 16px'
                        : '10px 16px 10px 16px'
                    }
                    quantity={quantity(item)}
                    title={item}
                  />
                  {item === 'Google Reviews' || item === 'Tripadvisor' ? (
                    isLoaded && (
                      <GoogleTripadvisorPopup
                        isOpen={activeTarget === index}
                        onClose={() => setActiveTarget(-1)}
                        setTargetValues={setTargetLinkValues}
                        targetName={item}
                        targetValues={targetLinkValues}
                      />
                    )
                  ) : (
                    <TargetPopup
                      excludeValues={excludeLinkValues}
                      isOpen={activeTarget === index}
                      onClose={() => setActiveTarget(-1)}
                      setExcludeValues={setExcludeLinkValues}
                      setTargetValues={setTargetLinkValues}
                      targetName={item}
                      targetValues={targetLinkValues}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.formSection}>
            <div className={styles.tagsLabelRow}>
              <div className={styles.sectionLabel}>{en.tags}</div>
              <span className={styles.tagsCounter}>
                {tags.length}&nbsp;{en.tagsAdded}
              </span>
            </div>

            <div className={styles.tagsInput}>
              <TagsInput
                addKeys={[ENTER_KEY_CODE]}
                inputProps={{
                  className: 'tags-input',
                  placeholder: en.enterTagsToFilter,
                }}
                inputValue={tagInput}
                onChange={(updatedTags) => handleAddTags(updatedTags)}
                onChangeInput={setTagInput}
                value={tags}
              />

              <Button
                className={styles.tagsAddButton}
                onClick={handleCreateTag}
                type="button"
              >
                {en.add}
              </Button>
            </div>
          </div>

          <div className={styles.formSection}>
            <IrrelevantKeywordsInput
              existingKeywords={addedKeywordsArr}
              existingTags={tags}
              irrelevantKeywords={irrelevantKeywords}
              onAddKeyword={handleAddIrrelevantKeyword}
              onRemoveKeyword={handleRemoveIrrelevantKeyword}
            />
          </div>

          <div className={styles.formSection}>
            <AddKeywordInput
              addNewKeyword={() => addNewKeyword()}
              currentKeywordInput={currentKeywordInput}
              keywordCount={addedKeywordsArr.length}
              setCurrentKeywordInput={setCurrentKeywordInput}
              variationsCount={registeredKeywordsArr.reduce(
                (count, keyword) => count + (keyword.variations?.length ?? 0),
                0
              )}
            />

            {addedKeywordsArr.length > 0 && (
              <div className={styles.keywordCardsList}>
                {addedKeywordsArr.map((item, index) => (
                  <KeywordFormCard
                    handleDelete={(keyword) => handleDeleteKeyword(keyword)}
                    key={item}
                    keyword={item}
                    onChangeValue={(data) =>
                      handleChangeKeywordSentimentsValue(data)
                    }
                    onKeywordUpdate={(keyword) =>
                      handleKeywordUpdate(keyword, index)
                    }
                    reservedKeywords={tags}
                  />
                ))}
              </div>
            )}
          </div>

          <div className={styles.actionsWrapper}>
            <div className={styles.actionsWrapperInner}>
              <button
                className={styles.saveAsDraftButton}
                onClick={(e) => handleSaveAsDraft(e)}
                type="button"
              >
                {en.saveAsDraft}
              </button>

              <Button
                className={styles.addButton}
                onClick={(e) => handleSaveCampaign(e)}
                type="button"
              >
                {en.add}
              </Button>
            </div>
          </div>
        </form>

        <div className={styles.relevancy}>
          {/* TODO: uncomment after relevancy functionality is ready */}
          {/* 
          {!!relevancyMutation.data && (
            <Relevancy
              isLoading={relevancyMutation.isLoading}
              onClickSuggestionItem={handleRelevancySuggestionClick}
              relevancyData={relevancyMutation.data}
            />
          )}
          */}
        </div>
      </div>
    </div>
  )
}
